import { Box } from "@mui/material";

import { colors } from "../theme/tokens";

import { LogoGrid, LogoStripHeading, LogoTile } from "./LogoStrip";

import type { Room } from "../schema";

import type { WidgetProps } from "./types";

// Logo strip of marketplaces (no tiles, no captions; every logo opens the
// marketplace's site in a new tab; Bill 2026-09-07), with the Bison-owned
// storefronts that fit this supplier's category (props.dtc) in the same strip
// after the marketplaces (one section, no storefront heading; Bill
// 2026-09-09). The canonical widget carries the static list of every
// marketplace Bison sells on (logos from logos.bisoncommerce.com, the same
// list the public website renders); the provisioning skill adds the per-room
// `dtc` band (Bison Baby for a nursery supplier, Bison Furniture for a
// furniture one, and so on).
//
// Since 2026-10-26 the strip is the shared LogoStrip (./LogoStrip.tsx), built
// like the website's MarketplaceStrip. The 58px marks under a 134px width cap that this replaced drew
// Lowe's, Best Buy and eBay large and Home Depot and Mathis Home small, with
// uneven gaps. Per-marketplace sizes copy the site's hand tuning and win over
// a stored `logoScale` (rooms stored 0.85 for marks that are no longer square
// badges); `logoScale` still sizes any other logo.
//
// A marketplace without a logoUrl falls back to a styled wordmark in the
// marketplace's known brand color. No third-party logo bitmaps ship from
// this repo.
export function MarketplaceGrid({ widget, room }: WidgetProps<"marketplace_grid">) {
    const { title, description, marketplaces } = widget.props;
    // An empty list drops the grid: a page replaces the canonical strip with its own.
    if (marketplaces.length === 0) return null;
    // The Welcome strip (canonical `where-we-sell`, 2026-09-09) carries no
    // dtc of its own: a room stores the band once, on the Capabilities page's
    // grid, and every other grid in the room borrows it.
    const dtc = widget.props.dtc ?? roomDtc(room);

    return (
        <Box>
            {/* An empty title lets the page copy above lead into the logos (2026-10-27). */}
            {title && <LogoStripHeading title={title} description={description} />}
            <LogoGrid count={marketplaces.length + (dtc?.sites.length ?? 0)}>
                {marketplaces.map((m) => (
                    <LogoTile
                        key={m.name}
                        name={m.name}
                        logoUrl={m.logoUrl}
                        href={m.href}
                        scale={SITE_SCALE[m.name] ?? m.logoScale}
                        color={m.brandColor ?? BRAND_COLORS[m.name] ?? colors.navy}
                    />
                ))}
                {/* The storefronts sit in the same strip after the
                    marketplaces, no heading of their own (Bill, 2026-09-09);
                    the stored dtc.title / description are not rendered. */}
                {dtc?.sites.map((s) => (
                    <LogoTile
                        key={s.id}
                        name={s.name}
                        logoUrl={s.logoUrl}
                        href={s.href}
                        scale={s.logoScale}
                        color={colors.navy}
                    />
                ))}
            </LogoGrid>
        </Box>
    );
}

function roomDtc(room: Room) {
    for (const page of room.pages) {
        for (const w of page.widgets) {
            if (w.type === "marketplace_grid" && w.props.dtc) return w.props.dtc;
        }
    }
    return undefined;
}

// The site's per-marketplace tuning (MarketplaceStrip: `bigger` is h-12
// against h-10, `smaller` h-8): the square Home Depot badge and the light,
// very wide Mathis Home wordmark read small at the common height, the heavy
// Shopify and Kohl's marks read large.
const SITE_SCALE: Record<string, number> = {
    "The Home Depot": 1.2,
    "Mathis Home": 1.2,
    Shopify: 0.8,
    "Kohl's": 0.8,
};


// Best-known brand colors for marketplace wordmark fallbacks.
// Add/override per marketplace as needed. Defaults to navy if unknown.
const BRAND_COLORS: Record<string, string> = {
    Amazon: "#FF9900",
    "Amazon US": "#FF9900",
    "Amazon CA": "#FF9900",
    Walmart: "#0071CE",
    "Walmart Marketplace": "#0071CE",
    Target: "#CC0000",
    "Target+": "#CC0000",
    Wayfair: "#7B189F",
    "Home Depot": "#F96302",
    "The Home Depot": "#F96302",
    "Lowe's": "#004990",
    Lowes: "#004990",
    "Macy's": "#E12030",
    Macys: "#E12030",
    Costco: "#005CB9",
    "Costco Next": "#005CB9",
    "Best Buy": "#0046BE",
    eBay: "#E53238",
    "Kohl's": "#000000",
    "Google Shopping": "#4285F4",
    "TikTok Shop": "#000000",
    Shopify: "#5E8E3E",
    "Mathis Home": colors.navy,
    Mathis: colors.navy,
    "Office Depot": "#CC0000",
    Cymax: colors.navy,
    HomeSquare: colors.navy,
    Sauder: "#7C2D12",
};
